<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import AssistantForm from './AssistantForm.vue';
import BusinessPresetSelector from './BusinessPresetSelector.vue';
import BusinessPresetPreview from './BusinessPresetPreview.vue';
import BusinessPresetSummary from './BusinessPresetSummary.vue';

const props = defineProps({
  selectedAssistant: {
    type: Object,
    default: () => ({}),
  },
  type: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
});
const emit = defineEmits(['close', 'created']);
const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const assistantForm = ref(null);

// View state: 'select_preset' | 'preview_preset' | 'preset_summary' | 'form'
const initialView = props.type === 'edit' ? 'form' : 'select_preset';
const currentView = ref(initialView);
const selectedPreset = ref(null);
const presetResult = ref(null);
const isApplyingPreset = ref(false);

const updateAssistant = assistantDetails =>
  store.dispatch('captainAssistants/update', {
    id: props.selectedAssistant.id,
    ...assistantDetails,
  });

const i18nKey = computed(
  () => `CAPTAIN.ASSISTANTS.${props.type.toUpperCase()}`
);

const dialogTitle = computed(() => {
  if (props.type === 'edit') return t(`${i18nKey.value}.TITLE`);
  if (currentView.value === 'select_preset')
    return t('CAPTAIN.ASSISTANTS.PRESET.SELECT_TITLE');
  if (currentView.value === 'preview_preset')
    return selectedPreset.value?.title || t(`${i18nKey.value}.TITLE`);
  if (currentView.value === 'preset_summary')
    return t('CAPTAIN.ASSISTANTS.PRESET.SUMMARY_TITLE');
  return t(`${i18nKey.value}.TITLE`);
});

const dialogDescription = computed(() => {
  if (currentView.value === 'select_preset')
    return t('CAPTAIN.ASSISTANTS.PRESET.SELECT_SUBTITLE');
  if (currentView.value === 'preview_preset')
    return t('CAPTAIN.ASSISTANTS.PRESET.PREVIEW_SUBTITLE');
  if (currentView.value === 'preset_summary')
    return t('CAPTAIN.ASSISTANTS.PRESET.SUMMARY_SUBTITLE');
  return t('CAPTAIN.ASSISTANTS.FORM_DESCRIPTION');
});

const createAssistant = async assistantDetails => {
  try {
    const newAssistant = await store.dispatch(
      'captainAssistants/create',
      assistantDetails
    );
    emit('created', newAssistant);
  } catch (error) {
    const errorMessage = error?.message || t(`${i18nKey.value}.ERROR_MESSAGE`);
    useAlert(errorMessage);
  }
};

const handleSubmit = async updatedAssistant => {
  try {
    if (props.type === 'edit') {
      await updateAssistant(updatedAssistant);
    } else {
      await createAssistant(updatedAssistant);
    }
    useAlert(t(`${i18nKey.value}.SUCCESS_MESSAGE`));
    dialogRef.value.close();
  } catch (error) {
    const errorMessage = error?.message || t(`${i18nKey.value}.ERROR_MESSAGE`);
    useAlert(errorMessage);
  }
};

const handleSelectPreset = preset => {
  selectedPreset.value = preset;
  currentView.value = 'preview_preset';
};

const handleGoToForm = () => {
  selectedPreset.value = null;
  currentView.value = 'form';
};

const handleBackToSelector = () => {
  selectedPreset.value = null;
  currentView.value = 'select_preset';
};

const handleConfirmPreset = async () => {
  isApplyingPreset.value = true;
  try {
    const result = await store.dispatch(
      'captainAssistants/applyBusinessPreset',
      selectedPreset.value.preset_id
    );
    presetResult.value = result;
    currentView.value = 'preset_summary';
    useAlert(t('CAPTAIN.ASSISTANTS.PRESET.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('CAPTAIN.ASSISTANTS.PRESET.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    isApplyingPreset.value = false;
  }
};

const handlePresetDone = () => {
  emit('created', presetResult.value?.assistant);
  dialogRef.value.close();
};

const handleClose = () => {
  currentView.value = initialView;
  selectedPreset.value = null;
  presetResult.value = null;
  emit('close');
};

const handleCancel = () => {
  dialogRef.value.close();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="2xl"
    :title="dialogTitle"
    :description="dialogDescription"
    :show-cancel-button="false"
    :show-confirm-button="false"
    overflow-y-auto
    @close="handleClose"
  >
    <!-- Step 1: pick the business type -->
    <BusinessPresetSelector
      v-if="currentView === 'select_preset'"
      @select="handleSelectPreset"
      @scratch="handleGoToForm"
    />

    <!-- Step 2: review what the preset will create -->
    <BusinessPresetPreview
      v-else-if="currentView === 'preview_preset' && selectedPreset"
      :preset="selectedPreset"
      :is-creating="isApplyingPreset"
      @confirm="handleConfirmPreset"
      @back="handleBackToSelector"
    />

    <!-- Step 3: what was created and what is still pending -->
    <BusinessPresetSummary
      v-else-if="currentView === 'preset_summary' && presetResult"
      :result="presetResult"
      @done="handlePresetDone"
    />

    <!-- Blank assistant form -->
    <AssistantForm
      v-else
      ref="assistantForm"
      :mode="type"
      :assistant="selectedAssistant"
      @submit="handleSubmit"
      @cancel="handleCancel"
    />
    <template #footer />
  </Dialog>
</template>
